import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Upload, FileText, FileSpreadsheet, AlertTriangle, CheckCircle, RefreshCw, TrendingUp, Calendar, Mail, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { getAuthHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

// Document colors
const DOC_COLORS = {
  vendor: { border: "border-orange-500", bg: "bg-orange-500/10", text: "text-orange-400", label: "Vendor Quotation" },
  ivalue: { border: "border-blue-500", bg: "bg-blue-500/10", text: "text-blue-400", label: "iValue Quotation" },
  partner_po: { border: "border-emerald-500", bg: "bg-emerald-500/10", text: "text-emerald-400", label: "Partner PO" }
};

function RecentList({ items, onNavigate }) {
  return items.map((c) => (
    <div 
      key={c.id} 
      className="p-3 hover:bg-white/5 cursor-pointer transition-colors"
      onClick={() => onNavigate(c.id)}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-white">
            <span className="text-blue-400">{c.quotation_id}</span>
            <span className="text-zinc-500 text-xs ml-1">v{c.version}</span>
          </p>
          <p className="text-xs text-zinc-500">{c.company_name || "N/A"}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${c.total_discrepancies > 0 ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>
            {c.total_discrepancies}
          </span>
          <ChevronRight size={14} className="text-zinc-500" />
        </div>
      </div>
    </div>
  ));
}

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];
const isAllowedFile = (f) => ALLOWED_FILE_TYPES.includes(f.type) || /\.(pdf|xlsx|xls)$/i.test(f.name);
const isSpreadsheet = (f) => /\.(xlsx|xls)$/i.test(f.name);

function UploadZone({ label, files, onFilesChange, testId, colorScheme }) {
  const [isDrag, setIsDrag] = useState(false);
  const colors = DOC_COLORS[colorScheme] || DOC_COLORS.ivalue;

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const valid = incoming.filter(isAllowedFile);
    const rejected = incoming.length - valid.length;
    if (rejected > 0) {
      toast.error(`${rejected} file(s) rejected: only PDF or Excel files are allowed`);
    }
    if (valid.length > 0) onFilesChange([...files, ...valid]);
  };

  const removeAt = (idx) => onFilesChange(files.filter((_, i) => i !== idx));

  const handleDragOver = (e) => { e.preventDefault(); setIsDrag(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDrag(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDrag(false);
    addFiles(e.dataTransfer.files);
  };
  const handleChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  let cls = "relative flex-1 min-h-[140px] rounded-xl border-2 border-dashed transition-all ";
  if (isDrag) cls += `${colors.border} ${colors.bg}`;
  else if (files.length > 0) cls += `${colors.border}/50 ${colors.bg}`;
  else cls += "border-zinc-700 hover:border-zinc-500 bg-zinc-900/30";

  return (
    <div
      data-testid={testId}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cls}
    >
      <input
        type="file"
        accept=".pdf,.xlsx,.xls"
        multiple
        onChange={handleChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center p-3 pointer-events-none overflow-auto">
        {files.length > 0 ? (
          <div className="relative z-20 w-full pointer-events-auto">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle className={colors.text} size={16} />
              <span className={`text-xs ${colors.text}`}>{label}</span>
            </div>
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-1 text-xs text-zinc-300 px-1 py-0.5">
                {isSpreadsheet(f) ? <FileSpreadsheet size={12} className="shrink-0" /> : <FileText size={12} className="shrink-0" />}
                <span className="truncate flex-1">{f.name}</span>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="text-zinc-500 hover:text-red-400 shrink-0"
                  aria-label={`Remove ${f.name}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className={isDrag ? colors.text : "text-zinc-500"} size={20} />
            <p className={`text-xs mt-2 text-center ${colors.text}`}>{label}</p>
            <p className="text-[10px] mt-1 text-zinc-600">PDF or Excel, multiple files allowed</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const [vendor, setVendor] = useState([]);
  const [ivalue, setIvalue] = useState([]);
  const [po, setPo] = useState([]);
  const [qid, setQid] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() })
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  const handleCompare = async () => {
    if (!vendor.length || !ivalue.length || !po.length || !qid.trim()) {
      toast.error("Please fill Quotation ID and upload all 3 documents");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    vendor.forEach((f) => formData.append("vendor_quotation", f));
    ivalue.forEach((f) => formData.append("ivalue_quotation", f));
    po.forEach((f) => formData.append("partner_po", f));
    formData.append("quotation_id", qid.trim());
    formData.append("send_email", sendEmail);

    try {
      const res = await axios.post(`${API}/compare`, formData, { 
        headers: { "Content-Type": "multipart/form-data", ...getAuthHeaders() } 
      });
      toast.success(`Found ${res.data.total_discrepancies} findings`);
      navigate(`/result/${res.data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Comparison failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setVendor([]);
    setIvalue([]);
    setPo([]);
    setQid("");
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="max-w-5xl mx-auto">
        {!statsLoading && stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-xs">Total Comparisons</span>
                  <FileText className="text-blue-400" size={16} />
                </div>
                <p className="text-2xl font-bold text-white mt-1">{stats.total_comparisons}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-xs">This Week</span>
                  <TrendingUp className="text-emerald-400" size={16} />
                </div>
                <p className="text-2xl font-bold text-white mt-1">{stats.week_comparisons}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-xs">High Severity</span>
                  <AlertTriangle className="text-amber-400" size={16} />
                </div>
                <p className="text-2xl font-bold text-white mt-1">{stats.findings_breakdown?.high || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 text-xs">This Month</span>
                  <Calendar className="text-purple-400" size={16} />
                </div>
                <p className="text-2xl font-bold text-white mt-1">{stats.month_comparisons}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <div className="mb-4">
              <h1 className="text-3xl font-bold text-white">New Comparison</h1>
              <p className="text-zinc-500 mt-1">Upload Vendor Quotation, iValue Quotation, and Partner PO</p>
            </div>

            <Card className="bg-zinc-900/50 border-white/5">
              <CardContent className="p-6">
                <div className="mb-6">
                  <label className="text-xs text-zinc-500 mb-2 block">Quotation ID *</label>
                  <Input 
                    value={qid} 
                    onChange={(e) => setQid(e.target.value)} 
                    placeholder="e.g., QID-2024-001" 
                    className="bg-zinc-800 border-zinc-700 text-white max-w-xs" 
                    data-testid="quotation-id-input" 
                  />
                  <p className="text-xs text-zinc-600 mt-1">Version will auto-increment if same ID is used</p>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  <UploadZone label="Vendor Quotation" files={vendor} onFilesChange={setVendor} testId="upload-vendor" colorScheme="vendor" />
                  <UploadZone label="iValue Quotation" files={ivalue} onFilesChange={setIvalue} testId="upload-ivalue" colorScheme="ivalue" />
                  <UploadZone label="Partner PO" files={po} onFilesChange={setPo} testId="upload-po" colorScheme="partner_po" />
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                    <Checkbox 
                      checked={sendEmail} 
                      onCheckedChange={setSendEmail}
                      className="border-zinc-600"
                    />
                    <Mail size={14} />
                    Send email notification when complete
                  </label>
                  
                  <div className="flex gap-4">
                    {(vendor.length || ivalue.length || po.length || qid) && (
                      <Button variant="secondary" onClick={handleClear} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300">
                        Clear All
                      </Button>
                    )}
                    <Button
                      onClick={handleCompare}
                      disabled={!vendor.length || !ivalue.length || !po.length || !qid.trim() || loading}
                      className="bg-blue-600 hover:bg-blue-500 min-w-[140px]"
                      data-testid="compare-btn"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <RefreshCw className="animate-spin" size={16} />Analyzing...
                        </span>
                      ) : "Compare"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <Card className="bg-orange-500/5 border-orange-500/20">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-zinc-200 mb-1">Comparison 1</h3>
                  <p className="text-xs"><span className="text-orange-400">Vendor</span> <span className="text-zinc-500">vs</span> <span className="text-blue-400">iValue</span></p>
                  <p className="text-xs text-zinc-600 mt-1">Qty, Part#, Description</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-500/5 border-emerald-500/20">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-zinc-200 mb-1">Comparison 2</h3>
                  <p className="text-xs"><span className="text-blue-400">iValue</span> <span className="text-zinc-500">vs</span> <span className="text-emerald-400">Partner PO</span></p>
                  <p className="text-xs text-zinc-600 mt-1">Price, Credit, Flagged Words</p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-white mb-4">Recent Comparisons</h2>
            <Card className="bg-zinc-900/50 border-white/5">
              <CardContent className="p-0 divide-y divide-white/5">
                {stats && stats.recent_comparisons && stats.recent_comparisons.length > 0 ? (
                  <RecentList items={stats.recent_comparisons} onNavigate={(id) => navigate(`/result/${id}`)} />
                ) : (
                  <div className="p-6 text-center text-zinc-500 text-sm">
                    No recent comparisons
                  </div>
                )}
              </CardContent>
            </Card>
            <Button 
              variant="ghost" 
              className="w-full mt-2 text-zinc-400 hover:text-white"
              onClick={() => navigate("/history")}
            >
              View All History
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
